import React, { useCallback, useEffect, useMemo, useState } from 'react';
import DomPurify from 'dompurify';
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import DropZone from './RoomFeatureFilter/DropZone';
import HelpPanel from './RoomFeatureFilter/HelpPanel';
import FilterPreview from './RoomFeatureFilter/FilterPreview';
import { useSidePanel } from '../../../contexts/SidePanelContext';
import { Modification, useMetadata } from '../../../contexts/MetadataContext';
import { useService } from '../../../contexts/ServiceContext';
import DownloadButton from './RoomFeatureFilter/DownloadButton';
import { Download } from '@mui/icons-material';

const RoomFeaturesEditor: React.FC = () => {
  const { showPanel } = useSidePanel();
  const { currentMetadata } = useMetadata();
  const { post } = useService();

  const [isSaving, setIsSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [file, setFile] = useState<File | null>(null);

  // loading from RESIDENTIAL, which is currently the only template type with modifications
  const [filters, setFilters] = useState<Modification[]>(
    currentMetadata()?.template?.RESIDENTIAL?.modifications
      ?.removeFromLookups ?? [],
  );

  const hasNewData = useMemo(() => {
    const lookups =
      currentMetadata()?.template?.RESIDENTIAL?.modifications
        ?.removeFromLookups;
    // compare empty states separately, since they are different forms
    if (!filters.length && !lookups?.length) {
      return false;
    }
    return filters !== lookups;
  }, [
    filters,
    currentMetadata()?.template?.RESIDENTIAL?.modifications?.removeFromLookups,
  ]);

  const showHelpPanel = () => {
    showPanel('Filtering Room Features', <HelpPanel />);
  };

  const handleCloseToast = () => {
    setShowToast(false);
  };

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setToastMessage('Saving Changes...');
    setShowToast(true);

    try {
      const metadata = currentMetadata();
      if (!metadata) {
        setToastMessage('No metadata available');
        setShowToast(true);
        return;
      }
      const newMetadata = { ...metadata };
      if (newMetadata?.template) {
        Object.entries(metadata.template).forEach((entry) => {
          const template = entry[1];
          template.modifications = {
            removeFromLookups: filters,
          };
        });
      }

      const request = {
        name: 'bright',
        template: newMetadata.template,
      };

      await post('save-template', {}, request);

      setToastMessage('Changes Saved');
      setShowToast(true);
    } catch (error) {
      console.error('Error saving template:', error);
      setToastMessage('Error saving changes');
      setShowToast(true);
    } finally {
      setIsSaving(false);
    }
  }, [filters]);

  useEffect(() => {
    const extractText = async () => {
      const text = await file?.text();
      if (text) {
        const sanitizedText = DomPurify.sanitize(text);
        const filters = translateCSVToModifications(sanitizedText);
        if (!filters) {
          return;
        }
        setFilters(filters);
      }
    };
    extractText();
  }, [file]);

  const translateModificationsToCSV = (lookups: Modification[]) => {
    const output: any[] = [];
    lookups.forEach((lookup) => {
      const setIndex = lookup.match.set;
      if (output[setIndex]?.length) {
        // existing set found, append another room type to the first cell of the row
        const currentRoomType = output[setIndex][0];
        output[setIndex][0] = `${currentRoomType}.${lookup.match.value}`;
      } else {
        // create a new row for a new set
        output[setIndex] = [];
        output[setIndex].push(`${lookup.match.value}`);
        lookup.target.value.forEach((item: string) => {
          output[setIndex].push(`${item}`);
        });
      }
    });
    return output;
  };

  const translateCSVToModifications = (
    text: string | undefined,
  ): Modification[] | undefined => {
    if (!text) {
      return;
    }
    const rows = text.split('\n');
    const output: Modification[] = [];
    let multiSetIndex = 0;
    rows.forEach((row) => {
      const lookups = row.split(',');
      const matchField = 'ROOM_TYPE';
      const targetField = 'ROOM_FEATURES';
      // the first item in lookups is the match field value, i.e. Attic for Room or Broker for Open House
      const matchValue = lookups.shift();
      // trim row-ending token '\r' if it comes after a comma in the csv
      const filteredLookups = lookups.filter(
        (lookup) => lookup !== '' && lookup !== '\r',
      );

      const normalizedLookups = filteredLookups.map((lookup) => lookup.trim());

      // trim '\r' off the end of the last lookup, if it exists
      let lastLookup = normalizedLookups[normalizedLookups.length - 1];
      if (lastLookup.includes('\r')) {
        lastLookup = lastLookup.slice(0, -1);
      }

      if (matchField && matchValue && targetField) {
        // grab multiple field values that have the same set of lookups to be removed, expected to be separated by '.'
        if (matchValue.includes('.')) {
          const multiValueTypes = matchValue.split('.');
          multiValueTypes.forEach((value) => {
            const modification: Modification = {
              match: { field: '', value: '', set: multiSetIndex },
              target: { field: '', value: [] },
            };
            // structure the data
            modification.match.field = matchField;
            modification.match.value = value;
            modification.target.field = targetField;
            modification.target.value = normalizedLookups;
            output.push(modification);
          });
        } else {
          const modification: Modification = {
            match: { field: '', value: '', set: multiSetIndex },
            target: { field: '', value: [] },
          };
          modification.match.field = matchField;
          modification.match.value = matchValue;
          modification.target.field = targetField;
          modification.target.value = normalizedLookups;
          output.push(modification);
        }
        multiSetIndex++;
      } else {
        console.error('Missing one of matchField, matchValue, or targetField');
      }
    });

    return output;
  };

  return (
    <>
      <Typography variant="h5" gutterBottom>
        Room Features to Filter Out By Room Type{' '}
        <IconButton onClick={showHelpPanel} sx={{ color: 'primary.main' }}>
          <InfoOutlineIcon />
        </IconButton>
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h6">Upload a CSV</Typography>
          <DropZone
            setFile={setFile}
            acceptedTypes={{
              'text/csv': ['.csv'],
            }}
          />
          <DownloadButton
            data={translateModificationsToCSV(filters)}
            text="Current Data"
            icon={<Download />}
            disabled={!filters.length}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="h6">Preview</Typography>
          <FilterPreview filters={filters} />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleSave}
            disabled={isSaving || !hasNewData}
            startIcon={isSaving ? <CircularProgress size={20} /> : null}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Grid>
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          open={showToast}
          autoHideDuration={3000}
          onClose={handleCloseToast}
          message={toastMessage}
        />
      </Grid>
    </>
  );
};

export default React.memo(RoomFeaturesEditor);
